/**
 * State hashing — FNV-1a over a canonical field order.
 *
 * This exists so a replay desync is debuggable. Without it, a divergence
 * gives you two multi-thousand-tick runs and no way to find the first
 * differing tick; with a trail you bisect to a checkpoint in seconds.
 * It is also the mechanism that makes "all juice is render-layer" an
 * enforceable claim rather than an intention: the trail must be identical
 * with the renderer running and without it.
 */
import type { RunState } from './state.js';

let h = 0;

const mix = (v: number): void => {
  // Fold a float into 4 bytes of entropy without transcendentals.
  const scaled = (v * 4096) | 0;
  h ^= scaled & 0xff; h = Math.imul(h, 0x01000193) >>> 0;
  h ^= (scaled >>> 8) & 0xff; h = Math.imul(h, 0x01000193) >>> 0;
  h ^= (scaled >>> 16) & 0xff; h = Math.imul(h, 0x01000193) >>> 0;
  h ^= (scaled >>> 24) & 0xff; h = Math.imul(h, 0x01000193) >>> 0;
};

export function hashState(s: RunState): number {
  h = 0x811c9dc5;

  mix(s.tick); mix(s.phase); mix(s.outcome); mix(s.worldY);
  mix(s.squadSize); mix(s.squadHpPool); mix(s.playerX); mix(s.playerY);
  mix(s.level); mix(s.xp); mix(s.xpToNext); mix(s.killCount); mix(s.damageDealt);
  mix(s.bossIdx); mix(s.bossPhase); mix(s.waveCursor); mix(s.gateCursor);
  mix(s.pendingLevelUps); mix(s.weaponIdx); mix(s.fireCooldown);
  mix(s.offerKind); mix(s.offerCount);

  for (let i = 0; i < s.rng.length; i++) mix(s.rng[i]);
  for (let i = 0; i < s.upgradeStacks.length; i++) mix(s.upgradeStacks[i]);
  for (let i = 0; i < s.offerCount; i++) mix(s.offerOptions[i]);

  // Pool index order — never insertion order, which depends on the
  // spawn/despawn history rather than on the state itself.
  for (let i = 0; i < s.ep.cap; i++) {
    if (!s.ep.alive[i]) continue;
    mix(i); mix(s.eX[i]); mix(s.eY[i]); mix(s.eHp[i]); mix(s.eDefIdx[i]); mix(s.eCooldown[i]);
  }
  for (let i = 0; i < s.pp.cap; i++) {
    if (!s.pp.alive[i]) continue;
    mix(i); mix(s.pX[i]); mix(s.pY[i]); mix(s.pVx[i]); mix(s.pVy[i]);
    mix(s.pLife[i]); mix(s.pDamage[i]); mix(s.pPierce[i]);
  }
  for (let i = 0; i < s.qp.cap; i++) {
    if (!s.qp.alive[i]) continue;
    mix(i); mix(s.qX[i]); mix(s.qY[i]); mix(s.qVx[i]); mix(s.qVy[i]); mix(s.qLife[i]);
  }
  for (let i = 0; i < s.kp.cap; i++) {
    if (!s.kp.alive[i]) continue;
    mix(i); mix(s.kX[i]); mix(s.kY[i]); mix(s.kKind[i]); mix(s.kValue[i]);
  }
  for (let i = 0; i < s.gp.cap; i++) {
    if (!s.gp.alive[i]) continue;
    mix(i); mix(s.gY[i]); mix(s.gValue[i]); mix(s.gHp[i]); mix(s.gGroup[i]); mix(s.gOp[i]); mix(s.gLane[i]);
  }
  for (let i = 0; i < s.sp.cap; i++) {
    if (!s.sp.alive[i]) continue;
    mix(i); mix(s.sX[i]); mix(s.sY[i]);
  }

  return h >>> 0;
}
